import { computed, nextTick, ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray, isFunction } from '@/global/utils/utils'
import { notify } from '@/global/services/notification/notificationBus'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
// The map draws the streets of the task behind a request, which is the very thing the
// task screens draw - so it is the same panel rather than a second one like it.
import TaskPathsMapPanel from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskPathsMapPanel.vue'
import { useTaskStreetPaths } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskStreetPaths'
import { TASKS_AUTOCOMPLETE_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'
import {
  hasBlockedVehicle,
  hasInactiveDriver,
  isAbnormal
} from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/requestData'
import { SCREEN_VIEWS } from '@/global/composables/table/useScreenView'
import { requestsScreenView } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useScreenView'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  ABNORMAL_ROW_COLOR,
  ABNORMAL_VALUE_COLOR,
  buildRequestEndpoint,
  REQUESTS_ENDPOINT,
  REQUESTS_TABLE_KEY,
  REQUESTS_TO_WORK_ORDERS_ENDPOINT,
  REQUESTS_TRANSLATION_NAMESPACE as NS,
  REQUESTS_UI_VIEW,
  REQUEST_CREATE_ROUTE,
  REQUEST_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/requests/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: REQUESTS_ENDPOINT
  }
}

/**
 * The requests listing.
 *
 * Besides showing the requests it is also where they become work orders: the
 * `Convert to work orders` button puts the table into a picking mode - checkboxes on the
 * rows, the route of whatever is ticked drawn on a map beside them - and the second
 * press on it asks for the start time and sends the lot.
 */
export function useTable (options) {
  const {
    trans,
    router,
    openConvertDialogFn,
    closeConvertDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  const { loadStreets } = useTaskStreetPaths()

  // Picking mode: the checkboxes and the map are only there while requests are being
  // chosen for conversion.
  const isConverting = ref(false)
  const shouldResetSelections = ref(false)
  const selectedRequests = ref([])
  const selectedStreets = ref([])

  const selectedRequestIds = computed(() => selectedRequests.value
    .map(request => Number(request?.id))
    .filter(id => !!id)
  )

  // The map only takes room while requests are being picked, whatever the picker was
  // last left on.
  const isMapVisible = computed(() => isConverting.value && requestsScreenView.isPanelVisible.value)

  // Same reason the other way round: with no map to leave in its place, the listing
  // stays whatever view is chosen.
  const isTableHidden = computed(() => isConverting.value && !requestsScreenView.isTableVisible.value)

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[REQUESTS_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onShowRequestDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: REQUEST_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  const onCreateNewRequest = () => {
    router.push({ name: REQUEST_CREATE_ROUTE })
  }

  const deleteRequest = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn(true)
      await api(WINTER_SERVICE_MODULE).delete(buildRequestEndpoint(id))
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onRequestDelete = (event) => {
    openInfoDialogFn({
      title: trans(`${NS}.delete_confirmation_title`),
      message: trans(`${NS}.delete_confirmation_message`),
      onConfirm: () => deleteRequest(event)
    })
  }

  /**
   * The listing reports no geometry, so ticking a request off means fetching the streets
   * of the task behind it - once per task, and remembered afterwards.
   */
  const onSelectionsChange = async (event) => {
    selectedRequests.value = isArray(event?.data) ? event.data : []

    const taskIds = selectedRequests.value
      .map(request => Number(request?.task_id ?? request?.foreign_id))
      .filter(id => !!id)

    selectedStreets.value = await loadStreets(taskIds)
  }

  const stopConverting = async () => {
    isConverting.value = false
    // The split only exists while requests are being picked, so the next time the mode
    // opens it starts on the full-width table again.
    requestsScreenView.setScreenView(SCREEN_VIEWS.table)
    selectedRequests.value = []
    selectedStreets.value = []
    // DataTable clears its own selection state when it sees this turn truthy, so the
    // flag has to be lowered again - but only after the watcher it drives has run.
    shouldResetSelections.value = true
    await nextTick()
    shouldResetSelections.value = false
  }

  const convertToWorkOrders = async (plannedStart) => {
    try {
      await api(WINTER_SERVICE_MODULE).post(REQUESTS_TO_WORK_ORDERS_ENDPOINT, {
        incoming_request_ids: selectedRequestIds.value,
        planned_start: plannedStart
      })

      closeConvertDialogFn?.()
      stopConverting()
      fetchTableData()
    }
    catch (exception) {
      // The api layer has already reported what the backend complained about; the
      // picking mode stays open so the selection can be corrected.
      console.error(exception)
    }
  }

  const onConvertToWorkOrders = () => {
    if (!isConverting.value) {
      isConverting.value = true
      // The design opens on the full-width table, the map one click away.
      requestsScreenView.setScreenView(SCREEN_VIEWS.table)
      return
    }

    if (!selectedRequestIds.value.length) {
      notify(trans(`${NS}.select_at_least_one_request`))
      return
    }

    openConvertDialogFn?.({
      requestCount: selectedRequestIds.value.length,
      onConfirm: (plannedStart) => convertToWorkOrders(plannedStart)
    })
  }

  // The listing reports every codebook as its own object, so the columns read their
  // label off `description` rather than off a flat key.
  const codebookCell = (key) => ({
    [key]: {
      custom_display_value: (data) => data?.[key]?.description || data?.[key]?.name || ''
    }
  })

  // A vehicle that cannot take the request, or a driver who cannot, is painted red -
  // the value itself, not just the row, so it is clear which of the two is the problem.
  const abnormalValueStyle = (isAbnormalValue) => {
    if (!isAbnormalValue) {
      return {}
    }

    return {
      color: ABNORMAL_VALUE_COLOR
    }
  }

  const cellsConfig = computed(() => ({
    ...codebookCell('type'),
    ...codebookCell('surface_type'),
    ...codebookCell('cleaning_type'),
    ...codebookCell('vehicle_type'),
    ...codebookCell('drive_type'),
    ...codebookCell('spread_type'),
    spread_quantity: {
      custom_display_value: (data) => data?.spread_quantity ?? '-'
    },
    vehicle: {
      custom_display_value: (data) => data?.vehicle?.registration || '',
      style: (data) => abnormalValueStyle(hasBlockedVehicle(data))
    },
    driver: {
      custom_display_value: (data) => data?.driver?.full_name || '',
      style: (data) => abnormalValueStyle(hasInactiveDriver(data))
    }
  }))

  // Only what the listing reports, and `sortable` only where it can order by it.
  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.task_type`),
      key: 'type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.surface_type`),
      key: 'surface_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.cleaning_type`),
      key: 'cleaning_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.vehicle_type`),
      key: 'vehicle_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.drive_type`),
      key: 'drive_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.spread_type`),
      key: 'spread_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.spread_quantity`),
      key: 'spread_quantity',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.registration`),
      key: 'vehicle',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.driver`),
      key: 'driver',
      sortable: true,
      always_visible: true
    }
  ]))

  // Only the filters the listing endpoint actually understands.
  const filtersConfig = computed(() => ({
    fields: [
      {
        // Picked by name rather than typed as an id: the listing shows a task's id, but
        // that is not what anyone knows a task by.
        key: 'task_id',
        label: trans(`${NS}.task`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: TASKS_AUTOCOMPLETE_ENDPOINT
        }
      },
      {
        key: 'base_id',
        label: trans(`${NS}.base`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: 'winter-service-bases/autocomplete'
        }
      }
    ]
  }))

  // Show details and delete only - a request is edited on its own screen, which the
  // details action is the way into.
  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowRequestDetails),
    deleteAction(trans, onRequestDelete)
  ]))

  const screenViewComponents = computed(() => {
    if (!isConverting.value) {
      return []
    }

    return [
      {
        component: requestsScreenView.toggle,
        // DataTable renders the grid-switcher and column-visibility icons ahead of this
        // slot and gives them no order of their own, so any negative value pulls this
        // in front of them.
        order: -1
      }
    ]
  })

  const additionalButtons = computed(() => ([
    {
      // Leaving the picking mode. Only offered while in it, next to the confirm.
      type: 'cancel',
      label: trans('base.cancel'),
      visible: () => isConverting.value,
      click: stopConverting
    },
    {
      // The first press opens the picking mode, the second confirms what was picked -
      // which is why it turns green once requests can be ticked.
      type: isConverting.value ? 'green-save' : 'save',
      label: trans(`${NS}.convert_to_work_orders`),
      click: onConvertToWorkOrders
    }
  ]))

  const abnormalRowStyle = ({ rowData }) => {
    if (!isAbnormal(rowData)) {
      return {}
    }

    return {
      backgroundColor: ABNORMAL_ROW_COLOR
    }
  }

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.requests')
      },
      {
        text: trans(`${NS}.overview`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: REQUESTS_TABLE_KEY,
      uiView: REQUESTS_UI_VIEW,
      apiConfig: API_CONFIG,
      // Off while requests are being picked, along with the details action above: a row
      // is ticked there, and a click that navigates away would throw the selection away
      // with it.
      rowDblClick: isConverting.value ? null : onShowRequestDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      search_and_buttons_config: {
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        additional_buttons: additionalButtons.value,
        // Hidden for now, on request: requests are only made out of tasks - the tasks
        // listing's `Convert to requests` - until the urgent, one-off kind is asked for
        // again. The screen behind it is left in place (RequestCreate), so turning this
        // back into `!isConverting.value` is all it takes to offer it again.
        showNewBtn: false,
        newClick: onCreateNewRequest,
        newButtonConfig: {
          label: trans(`${NS}.new_request`)
        }
      },
      rowsSelection: {
        showCheckboxes: isConverting.value,
        multiple: true,
        resetSelections: shouldResetSelections.value,
        // Keyed by the request's id rather than the row index, so a selection survives
        // paging and the map cannot disagree about which request is ticked.
        rememberSelectionByDataProperty: 'id',
        selectionsChange: onSelectionsChange
      },
      filters_row_end_components: screenViewComponents.value,
      // Gone only in the map-only view, and only while there is a map to leave in its
      // place.
      hideTable: isTableHidden.value,
      // The map shows the streets of every ticked request - there is nothing to draw
      // before the mode is open, and the picker above decides whether it takes room.
      sideTableComponent: {
        component: TaskPathsMapPanel,
        visible: isMapVisible.value,
        streets: selectedStreets.value
      },
      globalStyle: requestsScreenView.globalStyle.value,
      tableConfig: {
        // The whole actions column steps aside while requests are being picked: the
        // rows are there to be ticked, and both actions - opening a request and
        // deleting one - are about a single row rather than about the selection.
        displayActions: !isConverting.value,
        globalConfig: {
          // A request whose vehicle or driver can no longer take it is tinted, so it is
          // spotted before someone tries to make a work order out of it.
          rowStyle: abnormalRowStyle
        },
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
